<template>
  <div class="card mr-3 p-4 text-center">
    <div class="position-relative mb-3 d-flex align-items-center" style="height: 32px">
      <h3 class="text-muted mb-0 text-center w-100" style="font-size: 14px">Incidents</h3>
      <button
        v-if="isEnabled('ADVANCED_HEATMAP')"
        class="btn btn-outline-primary btn-sm position-absolute"
        style="top: 0; right: 0"
        @click="goToAdvancedHeatmap"
      >
        Explore full report
        <i class="far fa-arrow-up-right-from-square ml-1"></i>
      </button>
    </div>
    <apexchart
      :height="heatmapHeight + 'px'"
      width="95%"
      type="heatmap"
      :series="selectHeatmapSeries"
      :options="chartOptions"
    />
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'

const InsightsModule = createNamespacedHelpers('insightsv2')
const LocationModule = createNamespacedHelpers('location')
const FeatureFlagModule = createNamespacedHelpers('featureFlag')

export default {
  name: 'Heatmap',
  methods: {
    escapeHtml(unsafe) {
      try {
        const stringUnsafe = unsafe?.toString()
        const safe = stringUnsafe
          ?.replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
        return safe
      } catch (error) {
        console.error(error)
      }
    },
    goToAdvancedHeatmap() {
      this.$router.push('/reporting/advanced-heatmap')
    }
  },
  computed: {
    ...InsightsModule.mapGetters([
      'selectCategoryName',
      'selectHeatmapCategories',
      'selectHeatmapSeries'
    ]),
    ...LocationModule.mapGetters(['selectLocationName']),
    ...FeatureFlagModule.mapGetters(['isEnabled']),
    heatmapHeight() {
      const cellHeight = 40
      const xAxisHeight = 68
      const projectedHeight = cellHeight * this.selectHeatmapSeries.length + xAxisHeight
      return projectedHeight
    },
    chartOptions() {
      return {
        chart: {
          type: 'heatmap',
          toolbar: { show: false },
          zoom: { enabled: false },
          events: {
            // this 'click' will be used if we support navigating on clicking lcoation name
            // click: (event, chartContext, config) => {
            //   if (event.path[3].className.baseVal === 'apexcharts-yaxis') {
            //     console.log(event.path[0].innerHTML)
            //   }
            // },
            dataPointSelection: (event, chartContext, config) => {
              // don't do anything if they click on the 'Feedback' column cell
              if (config.dataPointIndex !== 0) {
                this.$router.push({
                  path: '/insights/v2/details',
                  query: {
                    dateRange: this.$route.query.dateRange,
                    sentiment: 'NEGATIVE',
                    category: this.selectHeatmapCategories[config.dataPointIndex],
                    locs: !['All', 'Company-wide', 'Selected Locations'].includes(
                      this.selectHeatmapSeries[config.seriesIndex].name
                    )
                      ? this.selectHeatmapSeries[config.seriesIndex].locationId
                      : undefined,
                    c: this.$route.query.c,
                    sources: this.$route.query.sources,
                    keywords: this.$route.query.keywords
                  }
                })
              }
            }
          }
        },
        dataLabels: {
          enabled: true,
          style: {
            colors: ['#12263F']
          },
          formatter: (val, { seriesIndex }) => {
            return val === -1
              ? this.selectHeatmapSeries[seriesIndex].totalCount
              : val
              ? val + '%'
              : '0%'
          }
        },
        xaxis: {
          categories: this.selectHeatmapCategories,
          tooltip: { enabled: false },
          position: 'top',
          axisBorder: { show: false },
          axisTicks: { show: false },
          labels: {
            formatter: (val) => {
              // For the 'Feedback' column, return as is
              if (val === 'Feedback') return val
              // For other columns, return the label directly
              return this.selectCategoryName(val)
            },
            trim: true,
            rotate: 0, // Add rotation to prevent label overlap
            rotateAlways: false,
            hideOverlappingLabels: false,
            maxHeight: 100
          }
        },
        yaxis: {
          labels: {
            formatter: (val) => {
              // Use the label directly from the API data
              return this.escapeHtml(val)
            },
            minWidth: 80,
            maxWidth: 100
          },
          reversed: false // Ensure data is not reversed
        },
        legend: { show: false },
        tooltip: {
          enabled: true,
          custom: ({ series, seriesIndex, dataPointIndex, w }) => {
            const incidentCount = this.selectHeatmapSeries[seriesIndex].counts[dataPointIndex]
            const totalCount = this.selectHeatmapSeries[seriesIndex].totalCount
            return dataPointIndex !== 0
              ? `
              ${incidentCount} ${incidentCount === 1 ? 'incident' : 'incidents'}
              out of ${totalCount} ${totalCount === 1 ? 'piece' : 'pieces'} of feedback
            `
              : `${totalCount} ${totalCount === 1 ? 'piece' : 'pieces'} of feedback`
          }
        },
        plotOptions: {
          heatmap: {
            radius: 0,
            enableShades: false,
            colorScale: {
              ranges: [
                {
                  from: -1,
                  to: 0,
                  color: '#FFFFFF'
                },
                {
                  from: 1,
                  to: 5,
                  color: '#FED2DA'
                },
                {
                  from: 6,
                  to: 10,
                  color: '#FDA5B5'
                },
                {
                  from: 11,
                  to: 100,
                  color: '#FB4B6B'
                }
              ]
            }
          }
        }
      }
    }
  }
}
</script>

<style scoped>
#line {
  color: white;
}
.bg-light-green {
  background: #ccf7e5;
}
.bg-light-red {
  background: #ffcccb;
}
</style>
